import React from 'react';
import './style.scss';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Spinner, Table } from 'react-bootstrap';
import LastPageIcon from '@mui/icons-material/LastPage';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import useScheduled from './hooks';
import RenderVMTable from './RenderVMTable';
import Modal from '../../../../components/organisms/modals';
import { Delete, Pencil, PrintGray } from '../../../../components/atoms/icons';
import { firstLetterCapitalized, handleDayChecker } from '../../../../components/atoms/func';
import {
  ROUTE_STATUS, SCHEDULE_STATUS, successUpdateMsg,
} from '../../../../components/atoms/const';
import api from '../../../../components/api';

interface Props {
  selectedDate: { start: string, end: string };
}

function Scheduled(props: Props) {
  const { selectedDate } = props;
  const {
    resData, onNavigateEditSchedule, isLoading, arrayOfShow, handleShow,
    currentPage, onNextPage, onPreviousPage, data, deleteScheduleLoading,
    resultDeleteSchedule, onAfterDeleteSchedule, onDeleteSchedule, handleStatus,
    statusLoad,
  } = useScheduled(selectedDate);
  const renderTh = (title: string, style?: React.CSSProperties | undefined) => (
    <th style={style} className="thead-content"><div className="my-2 fz-14 fw-bold">{title}</div></th>
  );
  const renderTd = (title: string | number, style?: React.CSSProperties | undefined) => (
    <td style={style}><div className="my-2 fz-14 fw-normal">{title}</div></td>
  );
  const renderModalBody = () => {
    if (!resultDeleteSchedule) return 'Are you sure? Do you want to delete this schedule';
    return successUpdateMsg.SUCCESS_DELETE_SCHEDULE_MSG;
  };
  const getStatusAction = (status: number) => {
    if (status === ROUTE_STATUS.DEFAULT) {
      return {
        title: 'Set Picking Ready',
        body: 'Are you sure you want to set this route ready for picking?',
        successMessage: successUpdateMsg.SUCCESS_SET_PICKING_READY,
        icon: (
          <ShoppingCartIcon
            titleAccess="Set Picking Ready"
            sx={{ color: '#AEAEAE', fontSize: 22 }}
          />
        ),
      };
    }
    if (status === ROUTE_STATUS.READY_TO_PICKING) {
      return {
        title: 'Interrupt Picking',
        body: 'Picking is not finished. Are you sure you want to interrupt picking for this route and set it ready for the driver?',
        successMessage: successUpdateMsg.SUCCESS_SET_DELIVERY_READY,
        icon: (
          <LocalShippingIcon
            titleAccess="Interrupt Picking and Set Driver Ready"
            sx={{ color: '#D99000', marginTop: '-1px', fontSize: 22 }}
          />
        ),
      };
    }
    if (status === ROUTE_STATUS.FINISH_PICKING) {
      return {
        title: 'Set Driver Ready',
        body: 'Picking is finished. Are you sure you want to set this route ready for the driver?',
        successMessage: successUpdateMsg.SUCCESS_SET_DELIVERY_READY,
        icon: (
          <LocalShippingIcon
            titleAccess="Set Driver Ready"
            sx={{ color: '#52BF26', marginTop: '-1px', fontSize: 22 }}
          />
        ),
      };
    }
    return null;
  };

  const generatePDF = async (id: number) => {
    const pdfData = await api.schedules.getPDFByScheduleId(id);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(pdfData?.data);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();

    // cleanup
    setTimeout(() => {
      link?.parentNode?.removeChild(link);
    }, 3000);
  };

  return (
    <div className="table-container">
      <Table borderless responsive>
        <thead>
          <tr>
            <th style={{ width: '20px', border: 0 }}>&nbsp;</th>
            {renderTh('DATE', { minWidth: '100px', width: '15%' })}
            {renderTh('ROUTE', { minWidth: '150px', width: '15%' })}
            {renderTh('TIME RANGE', { minWidth: '150px', width: '15%' })}
            {renderTh('CLIENT', { minWidth: '150px', width: '25%' })}
            {renderTh('DRIVER', { minWidth: '100px', width: '10%' })}
            {renderTh('STATUS', { minWidth: '100px', width: '10%' })}
            {renderTh('ACTIONS', { minWidth: '50px', width: '10%', textAlign: 'right' })}
            <th className="thead-content">&nbsp;</th>
            <th style={{ width: '20px', border: 0 }}>&nbsp;</th>
          </tr>
        </thead>
        {resData && resData.length ? (
          resData.map((schedule, i) => {
            // Some API deployments omit nullable projection fields instead of
            // returning `status: null`. Treat both null and undefined as Default.
            const scheduleStatus = schedule.status ?? ROUTE_STATUS.DEFAULT;
            const statusAction = getStatusAction(scheduleStatus);
            return (
              <tbody key={schedule.id} className="fz-14">
                <tr className={(i % 2) ? 'tbody-content align-middle' : 'align-middle'}>
                  <td>&nbsp;</td>
                  {renderTd(handleDayChecker(`${schedule.date}T00:00:00.000`))}
                  {renderTd(schedule.name)}
                  {renderTd('8:30 AM - 9:30 AM')}
                  {renderTd(`Number of Clients: ${schedule.numberOfClients}`)}
                  {renderTd(`${firstLetterCapitalized(schedule.user.firstname) ?? ''} ${firstLetterCapitalized(schedule.user.lastname) ?? ''}`)}
                  <td>
                    <div className={`my-2 fz-14 fw-normal schedule-status schedule-status-${scheduleStatus}`}>
                      {SCHEDULE_STATUS.find((x) => x.value === scheduleStatus)?.label ?? 'Unknown'}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex my-2 justify-content-end w-100 px-1">
                      <button
                        type="button"
                        onClick={() => generatePDF(schedule.id)}
                        className="border-0 bg-transparent mx-2"
                      >
                        <PrintGray />
                      </button>
                      {statusAction ? (
                        <div className="me-2">
                          <Modal.Default
                            cancelLabel="No"
                            submitLabel="Yes"
                            title={statusAction.title}
                            body={resultDeleteSchedule ? statusAction.successMessage : statusAction.body}
                            dialogClassName="custom-modal-width"
                            onSubmit={() => handleStatus(schedule.id, scheduleStatus)}
                            isLoading={statusLoad}
                            result={resultDeleteSchedule}
                            onAfterResult={onAfterDeleteSchedule}
                          >
                            {statusAction.icon}
                          </Modal.Default>
                        </div>
                      ) : null}
                      <Modal.Default
                        cancelLabel="No"
                        submitLabel="Yes"
                        title="Delete Schedule"
                        body={renderModalBody()}
                        dialogClassName="custom-modal-width"
                        onSubmit={() => onDeleteSchedule(schedule.id)}
                        isLoading={deleteScheduleLoading}
                        result={resultDeleteSchedule}
                        onAfterResult={onAfterDeleteSchedule}
                      >
                        <div style={{ marginBottom: '2px' }}>
                          <Delete width={18} />
                        </div>
                      </Modal.Default>
                      <button
                        type="button"
                        onClick={() => onNavigateEditSchedule(schedule.id)}
                        className="border-0 bg-transparent ms-2"
                      >
                        <Pencil width={18} />
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {arrayOfShow[i] ? (
                        <KeyboardArrowUpIcon
                          sx={{ fontSize: '30px' }}
                          role="button"
                          onClick={() => handleShow(i)}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{ fontSize: '30px', color: '#AEAEAE' }}
                          role="button"
                          onClick={() => handleShow(i)}
                        />
                      )}
                    </div>
                  </td>
                  <td>&nbsp;</td>
                </tr>
                <tr className={arrayOfShow[i] ? '' : 'd-none'}>
                  <th
                    colSpan={9}
                    style={{
                      backgroundColor: '#F3F4F8', padding: 0,
                    }}
                  >
                    {schedule.numberOfClients ? (
                      <RenderVMTable
                        isShow={arrayOfShow[i]}
                        renderTh={renderTh}
                        renderTd={renderTd}
                        id={schedule.id.toString()}
                      />
                    ) : ''}
                  </th>
                </tr>
              </tbody>
            );
          })
        ) : (
          <tbody>
            <tr>
              <td colSpan={9}>
                <div className="d-flex justify-content-center my-2">
                  <div className={!isLoading ? '' : 'd-none'}>Data Not Found</div>
                  <Spinner className={isLoading ? 'fz-14' : 'd-none'} animation="border" size="sm" />
                </div>
              </td>
            </tr>
          </tbody>
        )}
      </Table>
      <div className="d-flex justify-content-end align-items-center" style={{ marginRight: '20px' }}>
        <div className="fz-16 me-2">
          {currentPage + 1}
          {' '}
          -
          {' '}
          {data?.data?.totalPages}
        </div>
        <FirstPageIcon sx={{ fontSize: '30px' }} />
        <ChevronLeftIcon
          sx={{ fontSize: '30px' }}
          role="button"
          onClick={onPreviousPage}
        />
        <div>{currentPage + 1}</div>
        <ChevronRightIcon
          sx={{ fontSize: '30px' }}
          role="button"
          onClick={onNextPage}
        />
        <LastPageIcon sx={{ fontSize: '30px' }} />
      </div>
    </div>
  );
}

export default Scheduled;
